import React, { useRef, useEffect, useState } from 'react';
import QRCode from 'qrcode';

// Same physical label size/layout as the regular Receipt component, just
// relabeled for a store-to-store parcel and item rendering adapted for
// parcel items (products/boxes, no price field).
// onConfirmComplete is optional. Pass it when opening this receipt from the
// "processing" queue (not-yet-packaged parcels) to show a confirm button
// that marks the parcel as packaged. Omit it (e.g. when reprinting an
// already-completed parcel) and only Print/Close show.
const StoreParcelReceipt = ({ order, onClose, onConfirmComplete }) => {
  const qrCanvasRef = useRef(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleConfirm = async () => {
    if (isCompleting) return;
    setIsCompleting(true);
    try {
      await onConfirmComplete(order.id);
    } finally {
      setIsCompleting(false);
    }
  };

  useEffect(() => {
    if (qrCanvasRef.current) {
      const qrValue = `${order.steadfastConsignmentId || ''}-${order.orderId}`;
      QRCode.toCanvas(qrCanvasRef.current, qrValue, {
        width: 90,
        margin: 0,
        errorCorrectionLevel: 'M'
      });
    }
  }, [order]);

  const printInNewWindow = () => {
    let qrDataUrl = '';
    if (qrCanvasRef.current) {
      qrDataUrl = qrCanvasRef.current.toDataURL('image/png');
    }

    const printWindow = window.open('', '_blank', 'width=400,height=600');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Parcel Receipt - ${order.steadfastConsignmentId || order.orderId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; font-weight: bold; }
          body {
            font-family: 'Courier New', monospace;
            display: flex; justify-content: center; align-items: center;
            min-height: 100vh; background: #f0f0f0; padding: 10px;
          }
          #receipt-container {
            width: 87.6mm; height: 101.6mm; background: white; position: relative;
            padding: 4mm; margin: 0 auto; box-shadow: 0 0 10px rgba(0,0,0,0.2);
            border: 1px solid #ddd; overflow: hidden;
          }
          .receipt-header { text-align: center; font-size: 18pt; font-weight: bold; margin-bottom: 2mm; padding: 0 2mm; }
          .receipt-subheader { text-align: center; font-size: 9pt; font-weight: bold; margin-bottom: 2mm; padding: 0 2mm; letter-spacing: 0.5px; }
          .receipt-divider { border: none; border-top: 2px solid #000; margin: 1.5mm 2mm; }
          .receipt-id { font-size: 12pt; font-weight: bold; margin-bottom: 1.5mm; margin-top: 1.5mm; text-align: center; padding: 0 2mm; }
          .receipt-phone { font-size: 10pt; font-weight: bold; margin-bottom: 1.5mm; padding: 0 2mm; }
          .customer-section { font-size: 9pt; font-weight: bold; line-height: 12pt; margin-bottom: 1mm; margin-top: 1mm; padding: 0 2mm; }
          .customer-phone { font-size: 11pt; font-weight: bold; margin-top: 1mm; margin-bottom: 1mm; }
          .customer-address { font-size: 9pt; font-weight: bold; line-height: 11pt; max-height: 22pt; overflow: hidden; }
          .info-line { font-size: 10pt; font-weight: bold; line-height: 12pt; margin-bottom: 1mm; margin-top: 1mm; display: flex; justify-content: space-between; align-items: center; padding: 0 2mm; }
          .website-inline { font-size: 8pt; font-weight: bold; }
          .qr-container { text-align: center; margin: 2mm auto 2mm auto; padding: 0 2mm; }
          .qr-container img { margin: 0 auto; display: block; width: 90px; height: 90px; }
          .qr-label { font-size: 2px; font-weight: bold; margin-top: 1mm; margin-left: 2mm; text-align: left; padding: 0 2mm; }
          .footer { font-size: 8pt; font-weight: bold; margin-top: 1.5mm; display: flex; justify-content: space-between; padding: 0 2mm; }
          .print-button {
            position: fixed; top: 20px; right: 20px; background: #3b82f6; color: white;
            border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-size: 16px;
            font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 1000; font-family: Arial, sans-serif;
          }
          .print-button:hover { background: #2563eb; }
          @media print {
            body { background: white; padding: 0; }
            #receipt-container { box-shadow: none; border: none; }
            .print-button { display: none; }
          }
          @page { size: 87.6mm 101.6mm; margin: 0; }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Receipt</button>

        <div id="receipt-container">
          <div class="receipt-header">Elevate Mart</div>
          <div class="receipt-subheader">STORE-TO-STORE PARCEL</div>

          <hr class="receipt-divider" />

          <div class="receipt-id">${order.steadfastConsignmentId || order.orderId}</div>

          <div class="receipt-phone">Call: +8809647001250</div>

          <div class="customer-section">
            <div>Receiving Store: ${order.customerInfo?.name || 'N/A'}</div>
            <div class="customer-phone">Phone: ${order.customerInfo?.phone || 'N/A'}</div>
            <div class="customer-address">Address: ${order.customerInfo?.address || 'N/A'}</div>
          </div>

          <div class="info-line">
            <div>COD: ${order.totalAmount ?? '0.00'}</div>
            <div class="website-inline">www.elevatemart.com.bd</div>
          </div>

          <div class="qr-container">
            <img src="${qrDataUrl}" alt="QR Code" />
          </div>

          <div class="qr-label">Order: ${order.steadfastConsignmentId || ''}-${order.orderId}</div>

          <div class="footer">
            <span>[Elevate Mart]</span>
            <span>${formatDateStatic()}</span>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const formatDate = () => formatDateStatic();

  const formatDateStatic = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
  };

  // Parcel items are either { itemType: 'product', name, quantity, weight,
  // image } or { itemType: 'box', label, quantity, weight, contents: [...] }
  // — never a price, so the summary below shows quantity/weight instead of
  // a line total.
  const renderSummaryItem = (item, index) => {
    if (item.itemType === 'box') {
      return (
        <div key={index} className="p-4 bg-gray-50 rounded-lg">
          <p className="font-semibold text-gray-800">📦 {item.label || 'Box'}</p>
          <p className="text-sm text-gray-600">
            {item.contents?.length || 0} product(s) inside{item.weight ? ` · ${item.weight} kg` : ''}
          </p>
        </div>
      );
    }
    return (
      <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <img
          src={item.image}
          alt={item.name}
          className="w-16 h-16 object-cover rounded"
          onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }}
        />
        <div className="flex-1">
          <p className="font-semibold text-gray-800">{item.name}</p>
          <p className="text-sm text-gray-600">
            Quantity: {item.quantity}{item.weight ? ` · ${item.weight} kg` : ''}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        * { font-weight: bold; }
        @media print {
          body { margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .print-container { display: block !important; }
        }
        @page { size: 87.6mm 101.6mm; margin: 0; }
        #receipt-container {
          width: 87.6mm; height: 101.6mm; background: white; position: relative;
          font-family: 'Courier New', monospace; padding: 4mm; margin: 0 auto;
          box-shadow: 0 0 10px rgba(0,0,0,0.1); border: 1px solid #e0e0e0; overflow: hidden;
        }
        .receipt-header { text-align: center; font-size: 18pt; font-weight: bold; margin-bottom: 2mm; padding: 0 2mm; }
        .receipt-subheader { text-align: center; font-size: 9pt; font-weight: bold; margin-bottom: 2mm; padding: 0 2mm; letter-spacing: 0.5px; }
        .receipt-divider { border: none; border-top: 2px solid #000; margin: 1.5mm 2mm; }
        .receipt-id { font-size: 12pt; font-weight: bold; margin-bottom: 1.5mm; margin-top: 1.5mm; text-align: center; padding: 0 2mm; }
        .receipt-phone { font-size: 10pt; font-weight: bold; margin-bottom: 1.5mm; padding: 0 2mm; }
        .customer-section { font-size: 9pt; font-weight: bold; line-height: 12pt; margin-bottom: 1mm; margin-top: 1mm; padding: 0 2mm; }
        .customer-phone { font-size: 11pt; font-weight: bold; margin-top: 1mm; margin-bottom: 1mm; }
        .customer-address { font-size: 9pt; font-weight: bold; line-height: 11pt; max-height: 22pt; overflow: hidden; }
        .info-line { font-size: 10pt; font-weight: bold; line-height: 12pt; margin-bottom: 1mm; margin-top: 1mm; display: flex; justify-content: space-between; align-items: center; padding: 0 2mm; }
        .website-inline { font-size: 8pt; font-weight: bold; }
        .qr-container { text-align: center; margin: 2mm auto 2mm auto; padding: 0 2mm; }
        .qr-container canvas { margin: 0 auto; display: block; }
        .qr-label { font-size: 2px; font-weight: bold; margin-top: 1mm; margin-left: 2mm; text-align: left; padding: 0 2mm; }
        .footer { font-size: 8pt; font-weight: bold; margin-top: 1.5mm; display: flex; justify-content: space-between; padding: 0 2mm; }
      `}</style>

      <div className="min-h-screen bg-gray-100 p-4 no-print">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Parcel Receipt Preview</h2>
              <div className="flex gap-2">
                <button
                  onClick={printInNewWindow}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                >
                  🖨️ Print Receipt
                </button>
                {onConfirmComplete && (
                  <button
                    onClick={handleConfirm}
                    disabled={isCompleting}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                  >
                    {isCompleting ? 'সম্পন্ন হচ্ছে...' : '✅ প্যাকেজিং সম্পন্ন করুন'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              <div id="receipt-container">
                <div className="receipt-header">Elevate Mart</div>
                <div className="receipt-subheader">STORE-TO-STORE PARCEL</div>

                <hr className="receipt-divider" />

                <div className="receipt-id">
                  {order.steadfastConsignmentId || order.orderId}
                </div>

                <div className="receipt-phone">Call: +8809647001250</div>

                <div className="customer-section">
                  <div>Receiving Store: {order.customerInfo?.name || 'N/A'}</div>
                  <div className="customer-phone">Phone: {order.customerInfo?.phone || 'N/A'}</div>
                  <div className="customer-address">Address: {order.customerInfo?.address || 'N/A'}</div>
                </div>

                <div className="info-line">
                  <div>COD: {order.totalAmount ?? '0.00'}</div>
                  <div className="website-inline">www.elevatemart.com.bd</div>
                </div>

                <div className="qr-container">
                  <canvas ref={qrCanvasRef}></canvas>
                </div>

                <div className="qr-label">
                  Order: {order.steadfastConsignmentId || ''}-{order.orderId}
                </div>

                <div className="footer">
                  <span>[Elevate Mart]</span>
                  <span>{formatDate()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Parcel Summary</h3>
            <div className="space-y-4">
              {order.cartItems?.map((item, index) => renderSummaryItem(item, index))}

              <div className="border-t pt-4">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total COD (Delivery Charge):</span>
                  <span>৳{order.totalAmount ?? '0'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StoreParcelReceipt;